// ui/src/components/manager/CoverageExceptionCard.jsx
/**
 * @file CoverageExceptionCard.jsx
 * @module ui/src/components/manager/CoverageExceptionCard
 * @description Renders operationally distinct team coverage exceptions with CA-1 escalation actions.
 *
 * @session-6-migration-flags
 * - None. This component consumes server-supplied exception and escalation context only.
 *
 * @locked-rules
 * - C-36: Unassigned, coverage gap, and data readiness failures are distinct states and must never collapse.
 * - DP-8: Never render modifier-level CA-2 approval language.
 * - XC-6: Economic zero, unavailable, and partial values must remain distinct if economic values are introduced.
 * - XC-7: State is expressed with explicit state labels and data-state attributes; color is reinforcement only.
 * - DP-10: This component performs no optimistic server mutation; escalation is delegated to the parent callback.
 *
 * @accessibility
 * - Exception collection uses role="list".
 * - Individual exceptions use role="listitem".
 * - Escalation buttons identify the affected rep in aria-label.
 *
 * @dependencies
 * - None.
 */

import PropTypes from 'prop-types';

/**
 * @typedef {Object} CoverageException
 * @property {string} exceptionId
 * @property {string} positionId
 * @property {string} fullName
 * @property {'unassigned'|'coverage_gap'|'data_readiness'} exceptionType
 * @property {string} detail
 * @property {Object} escalationContext
 * @property {string} escalationContext.positionId
 * @property {number} escalationContext.fiscalPeriodId
 * @property {'unassigned'|'coverage_gap'|'data_readiness'} escalationContext.issueType
 */

/**
 * @typedef {Object} CoverageExceptionCardProps
 * @property {CoverageException[]} exceptions
 * @property {function(escalationContext: Object): void} onEscalate
 */

const EXCEPTION_META = {
  unassigned: {
    label: 'Unassigned',
    icon: '!',
    tone: 'danger',
  },
  coverage_gap: {
    label: 'Coverage Gap',
    icon: '◷',
    tone: 'warning',
  },
  data_readiness: {
    label: 'Data Readiness',
    icon: '◫',
    tone: 'warning',
  },
};

function CoverageExceptionCard({ exceptions, onEscalate }) {
  if (exceptions.length === 0) {
    return (
      <section
        className="coverage-exception-card coverage-exception-card--empty"
        aria-label="Coverage exceptions"
        data-state="success"
      >
        <div className="coverage-exception-card__success-icon" aria-hidden="true">
          ✓
        </div>
        <div>
          <h3 className="coverage-exception-card__empty-title">
            No coverage exceptions
          </h3>
          <p className="coverage-exception-card__empty-copy">
            All reps have active plan coverage.
          </p>
        </div>

        <style>{styles}</style>
      </section>
    );
  }

  return (
    <section
      className="coverage-exception-card"
      aria-labelledby="coverage-exception-card-title"
    >
      <header className="coverage-exception-card__header">
        <div>
          <p className="coverage-exception-card__eyebrow">Coverage</p>
          <h3
            id="coverage-exception-card-title"
            className="coverage-exception-card__title"
          >
            Coverage exceptions
          </h3>
        </div>

        <span
          className="coverage-exception-card__count"
          aria-label={`${exceptions.length} coverage ${
            exceptions.length === 1 ? 'exception' : 'exceptions'
          }`}
        >
          {exceptions.length}
        </span>
      </header>

      <div
        className="coverage-exception-card__list"
        role="list"
        aria-label="Reps with coverage issues"
      >
        {exceptions.map((exception) => {
          const meta =
            EXCEPTION_META[exception.exceptionType] ??
            EXCEPTION_META.data_readiness;

          return (
            <article
              key={exception.exceptionId}
              className="coverage-exception-card__item"
              role="listitem"
              data-state={exception.exceptionType}
            >
              <div
                className={`coverage-exception-card__icon coverage-exception-card__icon--${meta.tone}`}
                aria-hidden="true"
              >
                {meta.icon}
              </div>

              <div className="coverage-exception-card__content">
                <div className="coverage-exception-card__item-heading">
                  <strong className="coverage-exception-card__name">
                    {exception.fullName}
                  </strong>

                  <span
                    className={`coverage-exception-card__badge coverage-exception-card__badge--${meta.tone}`}
                    data-state={exception.exceptionType}
                  >
                    {meta.label}
                  </span>
                </div>

                <p className="coverage-exception-card__detail">
                  {exception.detail}
                </p>

                <button
                  type="button"
                  className="secondary-button coverage-exception-card__action"
                  onClick={() => onEscalate(exception.escalationContext)}
                  aria-label={`Escalate ${exception.fullName} coverage issue to CA-1`}
                >
                  Escalate to CA-1
                </button>
              </div>
            </article>
          );
        })}
      </div>

      <style>{styles}</style>
    </section>
  );
}

CoverageExceptionCard.propTypes = {
  exceptions: PropTypes.arrayOf(
    PropTypes.shape({
      exceptionId: PropTypes.string.isRequired,
      positionId: PropTypes.string.isRequired,
      fullName: PropTypes.string.isRequired,
      exceptionType: PropTypes.oneOf([
        'unassigned',
        'coverage_gap',
        'data_readiness',
      ]).isRequired,
      detail: PropTypes.string.isRequired,
      escalationContext: PropTypes.shape({
        positionId: PropTypes.string.isRequired,
        fiscalPeriodId: PropTypes.number.isRequired,
        issueType: PropTypes.oneOf([
          'unassigned',
          'coverage_gap',
          'data_readiness',
        ]).isRequired,
      }).isRequired,
    })
  ).isRequired,
  onEscalate: PropTypes.func.isRequired,
};

const styles = `
  .coverage-exception-card {
    overflow: hidden;
    border: 1px solid rgb(var(--color-border));
    border-radius: 12px;
    background: rgb(var(--color-surface));
    color: rgb(var(--color-text));
  }

  .coverage-exception-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid rgb(var(--color-border));
  }

  .coverage-exception-card__eyebrow {
    margin: 0 0 2px;
    color: rgb(var(--color-text-muted));
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .coverage-exception-card__title,
  .coverage-exception-card__empty-title {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
  }

  .coverage-exception-card__count {
    min-width: 24px;
    padding: 2px 8px;
    border: 1px solid rgb(var(--color-danger) / 0.25);
    border-radius: 999px;
    background: rgb(var(--color-danger) / 0.06);
    color: rgb(var(--color-danger));
    font-size: 11px;
    font-weight: 600;
    text-align: center;
  }

  .coverage-exception-card__list {
    display: flex;
    flex-direction: column;
  }

  .coverage-exception-card__item {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr);
    gap: 10px;
    padding: 14px 16px;
    border-bottom: 1px solid rgb(var(--color-border));
  }

  .coverage-exception-card__item:last-child {
    border-bottom: 0;
  }

  .coverage-exception-card__icon {
    display: inline-flex;
    width: 24px;
    height: 24px;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
  }

  .coverage-exception-card__icon--danger {
    background: rgb(var(--color-danger) / 0.08);
    color: rgb(var(--color-danger));
  }

  .coverage-exception-card__icon--warning {
    background: rgb(var(--color-warning) / 0.1);
    color: rgb(var(--color-warning));
  }

  .coverage-exception-card__content {
    min-width: 0;
  }

  .coverage-exception-card__item-heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  .coverage-exception-card__name {
    font-size: 13px;
    font-weight: 600;
  }

  .coverage-exception-card__badge {
    display: inline-flex;
    align-items: center;
    min-height: 20px;
    padding: 2px 10px;
    border: 1px solid;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 500;
  }

  .coverage-exception-card__badge--danger {
    border-color: rgb(var(--color-danger) / 0.25);
    background: rgb(var(--color-danger) / 0.05);
    color: rgb(var(--color-danger));
  }

  .coverage-exception-card__badge--warning {
    border-color: rgb(var(--color-warning) / 0.3);
    background: rgb(var(--color-warning) / 0.08);
    color: rgb(var(--color-warning));
  }

  .coverage-exception-card__detail {
    margin: 6px 0 10px;
    color: rgb(var(--color-text-muted));
    font-size: 12px;
    line-height: 1.45;
  }

  .coverage-exception-card__action:focus-visible {
    outline: 2px solid rgb(var(--color-focus-ring));
    outline-offset: 2px;
    border-radius: 4px;
  }

  .coverage-exception-card--empty {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 24px 16px;
    border-style: dashed;
    background: rgb(var(--color-success) / 0.04);
  }

  .coverage-exception-card__success-icon {
    display: flex;
    width: 28px;
    height: 28px;
    flex: 0 0 28px;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: rgb(var(--color-success) / 0.1);
    color: rgb(var(--color-success));
    font-weight: 700;
  }

  .coverage-exception-card__empty-copy {
    margin: 3px 0 0;
    color: rgb(var(--color-text-muted));
    font-size: 12px;
  }
`;

export default CoverageExceptionCard;
